import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import "./PropertyDetailedPage.css";
import { IoBedOutline } from "react-icons/io5";
import { PiBathtub } from "react-icons/pi";
import { MdSquareFoot } from "react-icons/md";
import { SiLevelsdotfyi } from "react-icons/si";
import MeetingRoomTwoToneIcon from "@mui/icons-material/MeetingRoomTwoTone";
import HeaderPD from "../Parts/HeaderPD.jsx";
import Footer from "../Parts/Footer.jsx";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const PropertyDetailedPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const sliderRef = useRef(null);
  const virtualPortRef = useRef(null);

  const scrollToVirtualTour = () => {
    virtualPortRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const handleClick = () => {
    navigate("/contact-us");
  };

  const scrollLeft = () => {
    sliderRef.current.slickPrev();
  };

  const scrollRight = () => {
    sliderRef.current.slickNext();
  };

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await axios.get(
          `https://realo-realestate.com/api/api/Property/GetProperty/${id}`
        );
        setProperty(response.data);
      } catch (error) {
        console.error("Error fetching property data:", error);
      }
    };
    fetchPropertyData();
  }, [id]);

  if (!property) {
    return <div>Loading...</div>;
  }

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    centerMode: true,
  };

  const images = property.images.map((img) => img.imageUrl);

  const handleImageClick = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  const renderIcons = () => {
    switch (property.propertyType?.toLowerCase()) {
      case "house":
        return (
          <>
            <span className="info-item">
              <IoBedOutline className="icon" /> {property.bedrooms} Dhoma
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <PiBathtub className="icon" /> {property.bathrooms} Banjo
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <SiLevelsdotfyi className="icon" /> {property.floorLevel} Katet
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <MdSquareFoot className="icon" /> {property.squareFeet} m²
            </span>
          </>
        );

      case "apartment":
        return (
          <>
            <span className="info-item">
              <IoBedOutline className="icon" /> {property.bedrooms} Dhoma
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <PiBathtub className="icon" /> {property.bathrooms} Banjo
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <SiLevelsdotfyi className="icon" /> {property.floorLevel} Kati
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <MdSquareFoot className="icon" /> {property.squareFeet} m²
            </span>
          </>
        );

      case "office":
      case "store":
        return (
          <>
            <span className="info-item">
              <MeetingRoomTwoToneIcon className="icon" /> {property.spaces}{" "}
              Hapësira
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <PiBathtub className="icon" /> {property.bathrooms} Banjo
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <SiLevelsdotfyi className="icon" /> {property.floorLevel} Kati
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <MdSquareFoot className="icon" /> {property.squareFeet} m²
            </span>
          </>
        );

      case "land":
        return (
          <span className="info-item">
            <MdSquareFoot className="icon" /> {property.squareFeet} m²
          </span>
        );

      case "building":
        return (
          <>
            <span className="info-item">
              <MeetingRoomTwoToneIcon className="icon" /> {property.spaces}{" "}
              Hapësira
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <SiLevelsdotfyi className="icon" /> {property.floorLevel} Kati
            </span>
            <span className="divider">|</span>
            <span className="info-item">
              <MdSquareFoot className="icon" /> {property.squareFeet} m²
            </span>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="property-detailed-container">
      <HeaderPD />
      <div className="property-header">
        <div className="property-header-box">
          <h1>
            {property.title}, {property.city}
          </h1>
          <p className="property-price-PD">€{property.price}</p>
        </div>
        <div className="property-basic-info">{renderIcons()}</div>
      </div>

      <div className="property-slider">
        <Slider ref={sliderRef} {...sliderSettings}>
          {property.images.map((image, index) => (
            <div key={index}>
              <img
                src={image.imageUrl}
                alt={`Property ${index}`}
                className="slider-image"
                onClick={() => handleImageClick(index)}
              />
            </div>
          ))}
        </Slider>

        <button className="property-slider-arrow left" onClick={scrollLeft}>
          <IoIosArrowBack />
        </button>
        <button className="property-slider-arrow right" onClick={scrollRight}>
          <IoIosArrowForward />
        </button>
      </div>

      {isOpen && (
        <Lightbox
          mainSrc={images[photoIndex]}
          nextSrc={images[(photoIndex + 1) % images.length]}
          prevSrc={images[(photoIndex + images.length - 1) % images.length]}
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex((photoIndex + images.length - 1) % images.length)
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % images.length)
          }
        />
      )}

      <div className="navigation-bar">
        <button onClick={handleClick} className="book-viewing">
          Book A Viewing
        </button>
        <div className="nav-buttons">
          <a href="#floorplan">PLANIMETRIA</a>
          <a href="#epc">DETAJET</a>
          <a href="#virtualport" onClick={scrollToVirtualTour}>
            VIRTUAL TOUR
          </a>
        </div>
      </div>

      <div className="property-full-details-container">
        <div className="property-description">
          <h1>Informatat e Pronës</h1>
          <hr />
          <p>{property.description}</p>
          <div className="property-description-elements">
            <div>
              {property.address &&
                property.city &&
                property.state &&
                property.zipCode && (
                  <p>
                    <strong>Adresa :</strong> {property.address},{" "}
                    {property.city}, {property.state}, {property.zipCode}
                  </p>
                )}
              {property.bedrooms && (
                <p>
                  <strong>Dhomat :</strong> {property.bedrooms}
                  {property.bathrooms && (
                    <>
                      {" | "}
                      <strong>Banjot :</strong> {property.bathrooms}
                    </>
                  )}
                </p>
              )}
              {property.squareFeet && (
                <p>
                  <strong>Siperfaqja :</strong> {property.squareFeet} m²
                </p>
              )}
              {property.orientation &&
                property.orientation !== "N/A" &&
                property.orientation !== "string" && (
                  <p>
                    <strong>Orientimi :</strong> {property.orientation}
                  </p>
                )}
              {property.heatingSystem &&
                property.heatingSystem !== "N/A" &&
                property.heatingSystem !== "string" && (
                  <p>
                    <strong>Sistemi i Ngrohjes :</strong>{" "}
                    {property.heatingSystem}
                  </p>
                )}
              {property.furniture &&
                property.furniture !== "N/A" &&
                property.furniture !== "string" && (
                  <p>
                    <strong>Mobile :</strong> {property.furniture}
                  </p>
                )}
              {property.additionalFeatures &&
                property.additionalFeatures !== "N/A" &&
                property.additionalFeatures !== "string" && (
                  <p>
                    <strong>Vecori të Tjera :</strong>{" "}
                    {property.additionalFeatures}
                  </p>
                )}
            </div>
            <div>
              {property.hasOwnershipDocument !== undefined && (
                <p>
                  <strong>Fleta Poseduse:</strong>{" "}
                  {property.hasOwnershipDocument ? "Po" : "Jo"}
                </p>
              )}
              {property.floorLevel &&
                property.floorLevel !== "N/A" &&
                property.floorLevel !== "string" && (
                  <p>
                    <strong>Kati :</strong> {property.floorLevel}
                  </p>
                )}
              {property.neighborhood &&
                property.neighborhood !== "N/A" &&
                property.neighborhood !== "string" && (
                  <p>
                    <strong>Lagjia :</strong> {property.neighborhood}
                  </p>
                )}
              {property.builder &&
                property.builder !== "N/A" &&
                property.builder !== "string" && (
                  <p>
                    <strong>Ndertuesit :</strong> {property.builder}
                  </p>
                )}
              {property.complex &&
                property.complex !== "N/A" &&
                property.complex !== "string" && (
                  <p>
                    <strong>Kompleksi Banesor :</strong> {property.complex}
                  </p>
                )}
              {property.country &&
                property.country !== "N/A" &&
                property.country !== "string" && (
                  <p>
                    <strong>Shteti :</strong> {property.country}
                  </p>
                )}
            </div>
          </div>
        </div>
        <div className="property-map">
          <iframe
            title="Property Location"
            width="100%"
            height="100%"
            frameBorder="0"
            src={`https://www.google.com/maps?q=${property.latitude},${property.longitude}&z=15&output=embed`}
            allowFullScreen
          ></iframe>
        </div>
      </div>

      {property.interiorVideo && (
        <div
          className="property-virtual-tour"
          id="property-virtual-port"
          ref={virtualPortRef}
        >
          <h1>Property Virtual Tour</h1>
          <hr />
          <div className="matterport-tour">
            <iframe
              src={property.interiorVideo}
              width="100%"
              height="400"
              frameBorder="0"
              allowFullScreen
              title="Virtual Tour"
            ></iframe>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default PropertyDetailedPage;
